import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { SERVICES } from "@/data/content";

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="py-20 md:py-28 border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="overline text-terracotta mb-4">Services</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.98] text-ink">
              Six services.
              <br />
              <span className="italic font-light text-forest">One accountable partner.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-muted2 leading-relaxed">
              Every mandate is delivered by a specialist team, on our SLAs, with a single
              relationship manager for your account.
            </p>
          </div>
        </div>
      </section>

      {/* Service list */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              id={s.slug}
              data-testid={`service-${s.slug}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-10 py-20 border-b border-hairline last:border-b-0"
            >
              <div className="md:col-span-4">
                <div className="overline text-terracotta">{s.tag}</div>
                <h2 className="font-display text-3xl md:text-4xl font-medium mt-3 text-ink tracking-tight">
                  {s.title}
                </h2>
              </div>
              <div className="md:col-span-5">
                <p className="text-lg text-muted2 leading-relaxed">{s.excerpt}</p>
                <ul className="mt-8 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-forest mt-0.5 shrink-0" strokeWidth={1.5} />
                      <span className="text-ink">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:col-span-3 flex flex-col justify-between gap-6">
                <div className="font-display text-7xl md:text-8xl text-forest/10 leading-none">
                  0{i + 1}
                </div>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.slug}`}
                  className="btn-ghost self-start"
                >
                  Enquire <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 bg-[#F1EAEC]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <SectionHeading
            align="center"
            overline="Not sure where to start?"
            title="Tell us the problem. We'll shape the mandate."
          />
          <div className="mt-10 flex justify-center">
            <Link to="/contact" data-testid="services-final-cta" className="btn-primary">
              Talk to a workforce advisor <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
